import config from '../config/environment';
import supabase from '../config/supabase';
import { MAIN_GOAL_LABELS } from '../constants';
import type { PaginatedResponse } from '../types';
import type { ExerciseLookup } from '../types/exercise';
import type { MainGoal, TrainingLevel, TrainingLocation } from '../types/registration';
import {
  TRAINING_CATEGORY_LABELS,
  type TrainingCategory,
  type TrainingItem,
  type TrainingRecord,
  type TrainingSummaryResponse,
} from '../types/training';
import { handleServiceError } from '../utils/errorHandler';
import { ExerciseService } from './ExerciseService';

interface PaginatedTrainingRow {
  id: number;
  code?: string | null;
  name: string;
  category: TrainingCategory;
  objective: MainGoal[] | MainGoal;
  description: string;
  month_index: number | null;
  location: TrainingLocation | null;
  sessions_per_week: number | null;
  duration_minutes: number | null;
  group: string | null;
  splits: string[];
  is_active: boolean;
}

export interface ListTrainingsFilters {
  searchTerm?: string;
  groupIds?: string[];
}

interface TrainingSummaryExecutionPayload {
  sets: number | null;
  repetitions: number | null;
  rest: number | null;
  method?: string | null;
  details?: Array<{
    repetitionsMin: number | null;
    repetitionsMax: number | null;
    loadPercentage: number | null;
    rest: number | null;
    block: number | null;
  }>;
  methodConfig?: {
    roundRepetitions?: number | null;
    intraSetRest?: number | null;
    fixedRestMin?: number | null;
    fixedRestMax?: number | null;
  } | null;
  executionSpeed?: 'isometry' | 'cadence' | null;
  isometricSeconds?: number | null;
  concentricSeconds?: number | null;
  pauseSeconds?: number | null;
  eccentricSeconds?: number | null;
  executionSeconds?: number | null;
  exerciseType?: 'strength' | 'cardio' | 'flexibility' | null;
}

interface TrainingSummaryExercisePayload {
  exerciseId: number;
  execution: TrainingSummaryExecutionPayload;
}

interface TrainingSummaryBlockPayload {
  id: string;
  items: TrainingSummaryExercisePayload[];
}

interface TrainingSummaryRequestPayload {
  blocks: TrainingSummaryBlockPayload[];
  trainingLevel?: TrainingLevel | null;
  trainingLevels?: TrainingLevel[] | null;
}

const PAGINATED_TRAININGS_FUNCTION_NAME = 'list-paginated-trainings';
const MANAGE_TRAINING_FUNCTION_NAME = 'manage-training';
const CALCULATE_TRAINING_SUMMARY_FUNCTION_NAME = 'calculate-training-summary';
const DEFAULT_SUMMARY_VALUE = '—';

const buildMonthLabel = (monthIndex: number) => `Mês ${monthIndex}`;

const formatOptionalCount = (value: number | null) => (
  value === null ? DEFAULT_SUMMARY_VALUE : String(value)
);

const normalizeObjectiveValues = (value?: MainGoal[] | MainGoal | null): MainGoal[] => {
  if (Array.isArray(value)) {
    return value;
  }

  return value ? [value] : [];
};

const buildObjectiveLabel = (value?: MainGoal[] | MainGoal | null) => normalizeObjectiveValues(value)
  .map((objective) => MAIN_GOAL_LABELS[objective] ?? objective)
  .join(' • ');

const mapTrainingRowToItem = (
  row: PaginatedTrainingRow,
  splits: string[],
  primaryGroupName: string | null,
): TrainingItem => ({
  id: String(row.id),
  code: row.code ?? null,
  name: row.name,
  category: row.category,
  status: TRAINING_CATEGORY_LABELS[row.category] ?? row.category,
  splits,
  loadIntensity: DEFAULT_SUMMARY_VALUE,
  effortPerception: DEFAULT_SUMMARY_VALUE,
  totalVolume: DEFAULT_SUMMARY_VALUE,
  objective: normalizeObjectiveValues(row.objective),
  descriptionLabel: buildObjectiveLabel(row.objective),
  description: row.description,
  group: primaryGroupName ?? 'Não informado',
  month: row.month_index === null ? DEFAULT_SUMMARY_VALUE : buildMonthLabel(row.month_index),
  monthIndex: row.month_index ?? undefined,
  location: row.location ?? undefined,
  frequency: formatOptionalCount(row.sessions_per_week),
  sessionsPerWeek: row.sessions_per_week ?? undefined,
  duration: formatOptionalCount(row.duration_minutes),
  durationMinutes: row.duration_minutes ?? undefined,
  isActive: row.is_active,
  primaryMuscleGroupId: null,
});

class TrainingServiceClass {
  async getTrainingGroupOptions(): Promise<ExerciseLookup[]> {
    return ExerciseService.getMuscleGroups();
  }

  private async getFunctionHeaders(): Promise<Record<string, string>> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      apikey: config.supabaseAnonKey,
    };
  }

  private async handleFunctionError(response: Response, context: string): Promise<never> {
    const errorBody = await response.json().catch(() => ({}));
    handleServiceError(new Error(errorBody?.error ?? `Erro ao ${context}`), context);
  }

  async getTrainingById(trainingId: string): Promise<TrainingRecord | null> {
    const parsedTrainingId = Number.parseInt(trainingId, 10);

    if (!Number.isInteger(parsedTrainingId)) {
      return null;
    }

    const headers = await this.getFunctionHeaders();
    const url = new URL(`${config.supabaseFunctionsUrl}/${MANAGE_TRAINING_FUNCTION_NAME}`);
    url.searchParams.set('id', String(parsedTrainingId));

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      return this.handleFunctionError(response, 'carregar treino');
    }

    return await response.json() as TrainingRecord;
  }

  async saveTraining(training: TrainingRecord): Promise<TrainingRecord> {
    const headers = await this.getFunctionHeaders();
    const response = await fetch(`${config.supabaseFunctionsUrl}/${MANAGE_TRAINING_FUNCTION_NAME}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(training),
    });

    if (!response.ok) {
      return this.handleFunctionError(response, 'salvar treino');
    }

    return await response.json() as TrainingRecord;
  }

  async getTrainingSummary(payload: TrainingSummaryRequestPayload): Promise<TrainingSummaryResponse> {
    const headers = await this.getFunctionHeaders();
    const response = await fetch(`${config.supabaseFunctionsUrl}/${CALCULATE_TRAINING_SUMMARY_FUNCTION_NAME}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return this.handleFunctionError(response, 'calcular resumo do treino');
    }

    return await response.json() as TrainingSummaryResponse;
  }

  async inactivateTraining(trainingId: string): Promise<void> {
    const parsedTrainingId = Number.parseInt(trainingId, 10);

    if (!Number.isInteger(parsedTrainingId)) {
      throw new Error('Treino inválido para inativação.');
    }

    const headers = await this.getFunctionHeaders();
    const response = await fetch(`${config.supabaseFunctionsUrl}/${MANAGE_TRAINING_FUNCTION_NAME}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'inactivate',
        id: parsedTrainingId,
      }),
    });

    if (!response.ok) {
      return this.handleFunctionError(response, 'inativar treino');
    }
  }

  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: ListTrainingsFilters = {},
  ): Promise<PaginatedResponse<TrainingItem>> {
    const headers = await this.getFunctionHeaders();
    const url = new URL(`${config.supabaseFunctionsUrl}/${PAGINATED_TRAININGS_FUNCTION_NAME}`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));

    if (filters.searchTerm?.trim()) {
      url.searchParams.set('search', filters.searchTerm.trim());
    }

    if (filters.groupIds?.length) {
      url.searchParams.set('muscle_group_ids', filters.groupIds.join(','));
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return this.handleFunctionError(response, 'listar treinos');
    }

    const result = await response.json() as PaginatedResponse<PaginatedTrainingRow>;

    return {
      pagination: result.pagination,
      data: result.data.map((row) => mapTrainingRowToItem(row, row.splits, row.group ?? null)),
    };
  }
}

export const TrainingService = new TrainingServiceClass();
